import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Map } from "lucide-react";
import { api } from "../lib/api";
import EnquiryDialog from "../components/site/EnquiryDialog";
import { STATUS_COLORS, STATUS_LABELS } from "../lib/constants";

const InventoryPage = ({ mode }) => {
  const available = mode === "available";
  const statuses = available ? ["available"] : ["sold", "reserved", "conditional"];
  const [parcels, setParcels] = useState([]);
  const [stages, setStages] = useState([]);
  const [enq, setEnq] = useState(null);
  const [stageF, setStageF] = useState("all");

  useEffect(() => {
    api.get(`/parcels?status=${statuses.join(",")}`).then((r) => setParcels(r.data)).catch(() => {});
    api.get("/stages").then((r) => setStages(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [mode]);

  const stageName = (id) => stages.find((s) => s.id === id)?.name || "—";
  const stageNo = (p) => p.stage_number || stages.find((s) => s.id === p.stage_id)?.number;
  const rows = useMemo(() => {
    let r = [...parcels].sort((a, b) => (stageNo(a) - stageNo(b)) || ((a.lot_number || 0) - (b.lot_number || 0)));
    if (stageF !== "all") r = r.filter((p) => String(stageNo(p)) === stageF);
    return r;
    // eslint-disable-next-line
  }, [parcels, stageF, stages]);

  const stageOptions = [...new Set(parcels.map((p) => stageNo(p)).filter(Boolean))].sort((a, b) => a - b);

  return (
    <div className="pt-[64px] bg-[#12201A] min-h-screen">
      <div className="max-w-[1500px] mx-auto px-5 md:px-8 py-14">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-5">
          <div>
            <p className="eyebrow text-[#C9B79C] mb-3">{available ? "Now Selling" : "Track record"}</p>
            <h1 className="font-display font-light text-4xl md:text-5xl text-[#F3EEE3]">
              {available ? "Available Lots" : "Committed Lots · Homes Coming"}
            </h1>
            <p className="text-[#F3EEE3]/60 mt-3">{rows.length} lots{available ? " · currently in Stage 8" : ""}</p>
          </div>
          <div className="flex items-center gap-3">
            <select value={stageF} onChange={(e) => setStageF(e.target.value)} data-testid="stage-filter" className="bg-[#0F1B15] border border-[#F3EEE3]/15 rounded-lg px-4 py-2.5 text-[#F3EEE3] text-sm">
              <option value="all">All stages</option>
              {stageOptions.map((n) => <option key={n} value={String(n)}>Stage {n}</option>)}
            </select>
            <Link to="/master-plan" className="inline-flex items-center gap-2 border border-[#F3EEE3]/30 text-[#F3EEE3] px-4 py-2.5 rounded-lg text-sm hover:bg-[#F3EEE3]/10"><Map size={15} /> Map</Link>
          </div>
        </div>

        <div className="mt-10 overflow-x-auto rounded-2xl border border-[#F3EEE3]/12">
          <table className="w-full text-sm" data-testid="inventory-table">
            <thead>
              <tr className="text-left text-[#F3EEE3]/50 eyebrow text-[0.55rem] border-b border-[#F3EEE3]/12">
                <th className="px-5 py-4">Lot</th>
                <th className="px-5 py-4">Stage</th>
                <th className="px-5 py-4">Builder</th>
                <th className="px-5 py-4">Dimensions</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4" />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-b border-[#F3EEE3]/8 hover:bg-[#F3EEE3]/[0.03]">
                  <td className="px-5 py-4 text-[#F3EEE3] font-medium">{p.lot_number ?? p.parcel_no}</td>
                  <td className="px-5 py-4 text-[#F3EEE3]/70">{stageName(p.stage_id)}</td>
                  <td className="px-5 py-4 text-[#F3EEE3]/70">{p.builder_name || "—"}</td>
                  <td className="px-5 py-4 text-[#F3EEE3]/70">{p.dimensions || "—"}</td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-2 text-[#F3EEE3]/80">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: STATUS_COLORS[p.status] }} /> {STATUS_LABELS[p.status]}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    {available && (
                      <button onClick={() => setEnq(p)} data-testid={`enquire-${p.parcel_no}`} className="inline-flex items-center gap-1.5 text-[#C9B79C] hover:text-[#F3EEE3] text-xs">
                        <Send size={13} /> Enquire
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-12 text-center text-[#F3EEE3]/40">No lots match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-[0.62rem] tracking-widest uppercase text-[#F3EEE3]/40 mt-6">Prices available on request. Contact us for current pricing and availability.</p>
      </div>
      <EnquiryDialog open={!!enq} onClose={() => setEnq(null)} context={{ parcel_id: enq?.id, label: `Lot ${enq?.lot_number ?? enq?.parcel_no}`, source: mode }} />
    </div>
  );
};

export default InventoryPage;
